import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SEO } from "../components/SEO";
import { apiFetch } from "../lib/api";

interface CaseStudy {
  id: string;
  title: string;
  slug: string;
  clientName: string;
  clientSegment: string;
  clientSize: string;
  challenge: string;
  solution: string;
  results: string;
  testimonial: string | null;
  coverImage: string | null;
  images: string | null;
  metrics: string;
  publishedAt: string;
}

const segmentLabels: Record<string, string> = {
  varejo: "Varejo",
  industria: "Indústria",
  servicos: "Serviços",
  ecommerce: "E-commerce",
  franquias: "Franquias",
};

const sizeLabels: Record<string, string> = {
  micro: "Micro",
  small: "Pequena",
  medium: "Média",
  large: "Grande",
};

export default function CaseStudyDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [caseStudy, setCaseStudy] = useState<CaseStudy | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    apiFetch(`/api/cases/${slug}`)
      .then((json) => {
        if (json.success) {
          setCaseStudy(json.data);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  if (notFound || !caseStudy) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center">
          <h1 className="text-4xl font-bold">404</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Caso de sucesso não encontrado.
          </p>
          <a href="/casos-de-sucesso" className="mt-4 inline-block text-accent-600 hover:underline">
            ← Voltar para Cases
          </a>
        </div>
      </div>
    );
  }

  const metrics: Record<string, string> = caseStudy.metrics ? JSON.parse(caseStudy.metrics) : {};
  const images: string[] = caseStudy.images ? JSON.parse(caseStudy.images) : [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={caseStudy.title}
        description={`Caso de sucesso: ${caseStudy.clientName} - ${caseStudy.challenge.substring(0, 150)}...`}
        keywords={[caseStudy.clientSegment, "caso de sucesso", "zweb", caseStudy.clientName]}
        ogType="article"
        ogImage={caseStudy.coverImage || undefined}
        canonicalUrl={`/casos-de-sucesso/${caseStudy.slug}`}
      />

      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/" className="text-sm font-medium hover:text-accent-600">Home</a>
            <a href="/casos-de-sucesso" className="text-sm font-medium text-accent-600">Cases</a>
            <a href="/blog" className="text-sm font-medium hover:text-accent-600">Blog</a>
          </nav>
        </div>
      </header>

      <main className="container py-12">
        <article className="mx-auto max-w-4xl">
          <a href="/casos-de-sucesso" className="text-sm text-accent-600 hover:underline">
            ← Voltar para Cases
          </a>

          <div className="mt-6 flex items-center gap-3">
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800 dark:bg-accent-900 dark:text-accent-300">
              {segmentLabels[caseStudy.clientSegment] || caseStudy.clientSegment}
            </span>
            <span className="text-sm text-gray-500">
              {sizeLabels[caseStudy.clientSize] || caseStudy.clientSize}
            </span>
          </div>

          <h1 className="mt-4 text-4xl font-bold leading-tight">{caseStudy.title}</h1>

          <div className="mt-4 flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-lg font-bold text-accent-600 dark:bg-accent-900 dark:text-accent-300">
              {caseStudy.clientName.charAt(0)}
            </div>
            <div>
              <div className="font-semibold">{caseStudy.clientName}</div>
              <div className="text-sm text-gray-500">
                {segmentLabels[caseStudy.clientSegment]} • {sizeLabels[caseStudy.clientSize]}
              </div>
            </div>
          </div>

          {caseStudy.coverImage && (
            <img
              src={caseStudy.coverImage}
              alt={caseStudy.title}
              className="mt-8 w-full rounded-2xl object-cover"
            />
          )}

          {/* Métricas */}
          {Object.keys(metrics).length > 0 && (
            <div className="mt-8 grid grid-cols-2 gap-6 rounded-2xl border border-gray-200 bg-white p-8 dark:border-gray-800 dark:bg-gray-950 md:grid-cols-4">
              {Object.entries(metrics).map(([key, value]) => (
                <div key={key} className="text-center">
                  <div className="text-3xl font-bold text-accent-600">{value}</div>
                  <div className="mt-1 text-sm text-gray-500">{key}</div>
                </div>
              ))}
            </div>
          )}

          {/* Desafio */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold">O Desafio</h2>
            <div
              className="prose prose-lg mt-4 max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: caseStudy.challenge }}
            />
          </div>

          {/* Solução */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold">A Solução</h2>
            <div
              className="prose prose-lg mt-4 max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: caseStudy.solution }}
            />
          </div>

          {/* Resultados */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold">Resultados</h2>
            <div
              className="prose prose-lg mt-4 max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: caseStudy.results }}
            />
          </div>

          {/* Depoimento */}
          {caseStudy.testimonial && (
            <div className="mt-12 rounded-2xl bg-gradient-to-br from-accent-50 to-violet-50 p-8 dark:from-accent-900/20 dark:to-violet-900/20">
              <svg className="h-8 w-8 text-accent-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>
              <p className="mt-4 text-xl italic text-gray-700 dark:text-gray-300">
                "{caseStudy.testimonial}"
              </p>
              <div className="mt-4 font-semibold text-gray-900 dark:text-white">
                — {caseStudy.clientName}
              </div>
            </div>
          )}

          {/* Galeria de imagens */}
          {images.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold">Galeria</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {images.map((image: string, index: number) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${caseStudy.title} - Imagem ${index + 1}`}
                    className="rounded-2xl object-cover"
                  />
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-gradient-to-br from-accent-500 to-violet-600 p-8 text-center text-white">
            <h3 className="text-2xl font-bold">Quer resultados como estes?</h3>
            <p className="mt-2 text-accent-100">
              Solicite uma demonstração gratuita e descubra como o ZWeb pode transformar sua empresa.
            </p>
            <a
              href="/solicitar-demo"
              className="mt-6 inline-block rounded-lg bg-white px-6 py-3 font-medium text-accent-600 hover:bg-accent-50"
            >
              Solicitar Demonstração
            </a>
          </div>
        </article>
      </main>

      <footer className="border-t border-gray-200 bg-white py-8 dark:border-gray-800 dark:bg-gray-950">
        <div className="container text-center text-sm text-gray-600 dark:text-gray-400">
          © 2026 ZWeb. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}
